import { useCallback, useEffect, useRef } from 'react';
import { Canvas } from 'fabric';
import { playheadStore } from '../../playback';
import { getObjectsByLayerId } from '../../model/layer-id';
import { readCanonicalTransforms, writeTransform } from '../../model/converters/read-canonical-transforms';
import { createFrameResolver, type CanonicalTransforms } from '../../model/resolve-frame';
import type { PieceModel } from '../../model/types';
import type { TimeMs } from '../types';

type UsePlaybackFramesReturn = {
  /** Puts the canvas back the way the user has it. Saving needs this before reading the canvas. */
  restoreEditingState: () => void;
};

/**
 * Draws the piece at the current playhead, from the same resolver the export uses.
 * Visibility applies always, transforms only while playing, so a paused canvas
 * stays as the user left it. Animating overwrites what is being edited, so a run
 * keeps the transforms it started from and writes them back when it ends.
 */
export const usePlaybackFrames = (
  canvas: Canvas | null,
  model: PieceModel,
  isPlaying: boolean
): UsePlaybackFramesReturn => {
  const canonicalRef = useRef<CanonicalTransforms>({});

  // Keeps the snapshot on purpose: `pause()` lands a render later, so a save can
  // run while the playhead is still moving and the run will need it again.
  const restoreEditingState = useCallback(() => {
    if (!canvas) return;
    const canonical = canonicalRef.current;

    getObjectsByLayerId(canvas).forEach((obj, id) => {
      const layer = model.layers[id];
      if (layer) obj.visible = layer.visible;

      const transform = canonical[id];
      if (transform) writeTransform(obj, transform);
    });

    canvas.requestRenderAll();
  }, [canvas, model]);

  useEffect(() => {
    if (!canvas) return;

    if (isPlaying) readCanonicalTransforms(canvas, canonicalRef.current);
    const resolveFrame = createFrameResolver(model, canonicalRef.current);

    const applyAt = (t: TimeMs) => {
      const objects = getObjectsByLayerId(canvas);

      for (const layer of resolveFrame(t).layers) {
        const obj = objects.get(layer.id);
        if (!obj) continue;

        obj.visible = layer.visible;
        if (isPlaying) writeTransform(obj, layer.transform);
      }

      canvas.requestRenderAll();
    };

    applyAt(playheadStore.get());
    const unsubscribe = playheadStore.subscribe(() => applyAt(playheadStore.get()));

    return () => {
      unsubscribe();
      if (!isPlaying) return;
      restoreEditingState();
      canonicalRef.current = {};
    };
  }, [canvas, model, isPlaying, restoreEditingState]);

  return { restoreEditingState };
};
